/**
 * Helper functions for working with USAGE task chains
 */

// Find the tail (latest task) of a chain
export const findChainTail = (taskId, allTasks) => {
  let currentId = taskId
  let maxIterations = 100 // Prevent infinite loops

  for (let i = 0; i < maxIterations; i++) {
    // Find if there's a child task that links to current task
    const child = allTasks.find(t => t.usage_parenttask_uid == currentId)

    if (!child) {
      // No child found, this is the tail
      return currentId
    }

    // Move to the child
    currentId = child.uid
  }

  return currentId
}

// Find the head (base task) of a chain
export const findChainHead = (taskId, allTasks) => {
  const task = allTasks.find(t => t.uid == taskId)
  if (!task) {
    return taskId
  }

  let currentTask = task
  let maxIterations = 100 // Prevent infinite loops

  for (let i = 0; i < maxIterations; i++) {
    if (!currentTask.usage_parenttask_uid) {
      // No parent, this is the head
      return currentTask.uid
    }

    // Move to parent
    currentTask = allTasks.find(t => t.uid == currentTask.usage_parenttask_uid)
    if (!currentTask) {
      return taskId
    }
  }

  return currentTask.uid
}

// Get all chain tails (latest task from each chain)
export const getUsageChainTails = (tasks, USAGE_TYPE_ID = 12) => {
  const usageTasks = tasks.filter(t =>
    t.type == USAGE_TYPE_ID || t.type?.uid == USAGE_TYPE_ID || t.type === "USAGE"
  )

  const seenChains = new Set()
  const tails = []

  for (const task of usageTasks) {
    // Find the head of this task's chain
    const headId = findChainHead(task.uid, usageTasks)

    // Skip if we've already processed this chain
    if (seenChains.has(headId)) {
      continue
    }

    seenChains.add(headId)

    // Find the tail of this chain
    const tailId = findChainTail(headId, usageTasks)
    const tailTask = usageTasks.find(t => t.uid == tailId)

    if (tailTask) {
      tails.push(tailTask)
    }
  }

  return tails
}

// Get full chain from any task in the chain (walks backwards to base, returns array in chronological order)
export const getFullChain = (task, allTasks) => {
  if (!task) {
    return []
  }

  const chain = []
  let currentTask = task
  const visited = new Set()

  // Walk backwards to base
  while (currentTask && !visited.has(currentTask.uid)) {
    visited.add(currentTask.uid)
    chain.unshift(currentTask) // Add to front

    if (currentTask.usage_parenttask_uid) {
      currentTask = allTasks.find(t => t.uid === currentTask.usage_parenttask_uid)
    } else {
      break
    }
  }

  return chain
}
